import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

/**
 * DoorWindowModel supports two template layouts:
 *
 * Template1 (single door): [LeftFrame][LeftPanel][TopCenter+CenterDoor][RightPanel][RightFrame]
 * Template2 (double door): [LeftFrame][LeftPanel][TopDoor+Door1+Door2][RightPanel][RightFrame]
 *
 * Template detection is based on modelId.
 */

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

// ─── Template detection ───

function detectTemplate(model: ModelData): 'template1' | 'template2' {
  if (model?.modelId === 'model2') return 'template2'
  if (model?.modelId === 'model3') return 'template2'
  return 'template1'
}

// ─── Template1 position recalculation ───

function recalculateTemplate1Positions(model: ModelData) {
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const leftPanel = getParam(model.parameters!, 'left_glass_panel')
  const topCenterPanel = getParam(model.parameters!, 'top_center_glass_panel')
  const centerDoorPanel = getParam(model.parameters!, 'center_door_glass_panel')

  if (!leftFrame || !topFrame) return

  let currentX = leftFrame.width

  if (leftPanel) {
    updateParam(model, 'left_glass_panel', {
      position: { ...leftPanel.position, x: leftFrame.width, y: topFrame.height },
    })
    currentX += leftPanel.width
  }

  if (topCenterPanel) {
    updateParam(model, 'top_center_glass_panel', {
      position: { ...topCenterPanel.position, x: currentX },
    })
  }

  if (centerDoorPanel) {
    updateParam(model, 'center_door_glass_panel', {
      position: { ...centerDoorPanel.position, x: currentX },
    })
    currentX += centerDoorPanel.width
  }

  const rightPanel = getParam(model.parameters!, 'right_glass_panel')
  if (rightPanel) {
    updateParam(model, 'right_glass_panel', {
      position: { ...rightPanel.position, x: currentX },
    })
  }
}

// ─── Template2 position recalculation ───

function recalculateTemplate2Positions(model: ModelData) {
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const leftPanel = getParam(model.parameters!, 'left_glass_panel')
  const topDoorPanel = getParam(model.parameters!, 'top_door_glass_panel')
  const doorPanel1 = getParam(model.parameters!, 'door_glass_panel_1')
  const doorPanel2 = getParam(model.parameters!, 'door_glass_panel_2')

  if (!leftFrame || !topFrame) return

  let currentX = leftFrame.width

  if (leftPanel) {
    updateParam(model, 'left_glass_panel', {
      position: { ...leftPanel.position, x: leftFrame.width, y: topFrame.height },
    })
    currentX += leftPanel.width
  }

  if (topDoorPanel) {
    updateParam(model, 'top_door_glass_panel', {
      position: { ...topDoorPanel.position, x: currentX },
    })
  }

  if (doorPanel1) {
    updateParam(model, 'door_glass_panel_1', {
      position: { ...doorPanel1.position, x: currentX },
    })
  }

  if (doorPanel2) {
    const door1X = currentX + (doorPanel1?.width || 0)
    updateParam(model, 'door_glass_panel_2', {
      position: { ...doorPanel2.position, x: door1X },
    })
    currentX = door1X + doorPanel2.width
  }

  const rightPanel = getParam(model.parameters!, 'right_glass_panel')
  if (rightPanel) {
    updateParam(model, 'right_glass_panel', {
      position: { ...rightPanel.position, x: currentX },
    })
  }
}

// ─── Template1 dimension handlers ───

const template1TotalWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const currentTotalWidth = model.width!
  const widthDifference = numValue - currentTotalWidth
  const halfDifference = widthDifference / 2

  model.width = numValue
  updateParam(model, 'top_frame_profile', { width: numValue })

  if (cascadeUpdates) {
    const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
    const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')

    if (bottomLeft && bottomRight) {
      updateParam(model, 'bottom_left_frame_profile', {
        width: bottomLeft.width + halfDifference,
      })
      updateParam(model, 'bottom_right_frame_profile', {
        width: bottomRight.width + halfDifference,
      })
    }

    const leftPanel = getParam(model.parameters!, 'left_glass_panel')
    const rightPanel = getParam(model.parameters!, 'right_glass_panel')
    const topCenterPanel = getParam(model.parameters!, 'top_center_glass_panel')
    const centerDoorPanel = getParam(model.parameters!, 'center_door_glass_panel')

    if (leftPanel) {
      updateParam(model, 'left_glass_panel', { width: leftPanel.width + halfDifference })
    }
    if (rightPanel) {
      updateParam(model, 'right_glass_panel', { width: rightPanel.width + halfDifference })
    }
    if (topCenterPanel) {
      updateParam(model, 'top_center_glass_panel', {
        width: topCenterPanel.width + halfDifference,
      })
    }
    if (centerDoorPanel) {
      updateParam(model, 'center_door_glass_panel', {
        width: centerDoorPanel.width + halfDifference,
      })
    }

    recalculateTemplate1Positions(model)
  }

  return model
}

const template1TotalHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options

  model.height = numValue
  updateParam(model, 'left_frame_profile', { height: numValue })
  updateParam(model, 'right_frame_profile', { height: numValue })

  if (cascadeUpdates) {
    const topCenterPanel = getParam(model.parameters!, 'top_center_glass_panel')
    const topFrame = getParam(model.parameters!, 'top_frame_profile')

    if (topCenterPanel && topFrame) {
      const remainingHeight = numValue - topFrame.height * 2

      updateParam(model, 'left_glass_panel', { height: remainingHeight })
      updateParam(model, 'center_door_glass_panel', { height: numValue })
      updateParam(model, 'right_glass_panel', { height: remainingHeight })
    }
  }

  return model
}

const topCenterHeight: DimensionHandler = (model, _initial, numValue) => {
  const currentTopCenter = getParam(model.parameters!, 'top_center_glass_panel')
  const doorPanel = getParam(model.parameters!, 'center_door_glass_panel')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!currentTopCenter || !doorPanel || !topFrame) return model

  const heightDifference = numValue - currentTopCenter.height

  updateParam(model, 'top_center_glass_panel', { height: numValue })
  updateParam(model, 'center_door_glass_panel', {
    height: doorPanel.height - heightDifference,
  })
  updateParam(model, 'center_door_glass_panel', {
    position: { ...doorPanel.position, y: topFrame.height + numValue },
  })

  return model
}

const centerPanelHeight: DimensionHandler = (model, _initial, numValue) => {
  const doorPanel = getParam(model.parameters!, 'center_door_glass_panel')
  const topCenterPanel = getParam(model.parameters!, 'top_center_glass_panel')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!doorPanel || !topCenterPanel || !topFrame) return model

  const heightDifference = numValue - doorPanel.height

  updateParam(model, 'center_door_glass_panel', { height: numValue })
  updateParam(model, 'top_center_glass_panel', {
    height: topCenterPanel.height - heightDifference,
  })
  updateParam(model, 'center_door_glass_panel', {
    position: {
      ...doorPanel.position,
      y: topFrame.height + (topCenterPanel.height - heightDifference),
    },
  })

  return model
}

const parameterWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { positionId, cascadeUpdates = true } = options as {
    positionId?: string
    cascadeUpdates?: boolean
  }
  if (!positionId) return model

  const currentParameter = getParam(model.parameters!, positionId as string)
  if (!currentParameter) return model

  const currentWidth = currentParameter.width
  const widthDifference = numValue - currentWidth

  updateParam(model, positionId as string, { width: numValue })

  if (cascadeUpdates) {
    const templateType = detectTemplate(model)

    if (templateType === 'template1') {
      if (positionId === 'center_door_glass_panel') {
        model.doorConfiguration = {
          ...model.doorConfiguration!,
          totalDoorWidth: numValue,
          individualDoorWidth: [numValue],
        }
        updateParam(model, 'top_center_glass_panel', { width: numValue })
        model.width = model.width! + widthDifference
        updateParam(model, 'top_frame_profile', { width: model.width! })
      } else if (positionId === 'top_center_glass_panel') {
        updateParam(model, 'center_door_glass_panel', { width: numValue })
        model.doorConfiguration = {
          ...model.doorConfiguration!,
          totalDoorWidth: numValue,
          individualDoorWidth: [numValue],
        }
        model.width = model.width! + widthDifference
        updateParam(model, 'top_frame_profile', { width: model.width! })
      } else if (positionId === 'left_glass_panel') {
        const leftFrame = getParam(model.parameters!, 'left_frame_profile')
        if (leftFrame) {
          updateParam(model, 'bottom_left_frame_profile', {
            width: numValue + leftFrame.width,
          })
        }
        model.width = model.width! + widthDifference
        updateParam(model, 'top_frame_profile', { width: model.width! })
      } else if (positionId === 'right_glass_panel') {
        const rightFrame = getParam(model.parameters!, 'right_frame_profile')
        if (rightFrame) {
          updateParam(model, 'bottom_right_frame_profile', {
            width: numValue + rightFrame.width,
          })
        }
        model.width = model.width! + widthDifference
        updateParam(model, 'top_frame_profile', { width: model.width! })
      }
      recalculateTemplate1Positions(model)
    } else {
      // Template2
      if (positionId === 'door_glass_panel_1') {
        const doorPanel2 = getParam(model.parameters!, 'door_glass_panel_2')
        const newTotalDoorWidth = numValue + (doorPanel2?.width || 0)
        model.doorConfiguration = {
          ...model.doorConfiguration!,
          totalDoorWidth: newTotalDoorWidth,
          individualDoorWidth: [numValue, doorPanel2?.width || 0],
        }
        updateParam(model, 'top_door_glass_panel', { width: newTotalDoorWidth })
        model.width = model.width! + widthDifference
        updateParam(model, 'top_frame_profile', { width: model.width! })
      } else if (positionId === 'door_glass_panel_2') {
        const doorPanel1 = getParam(model.parameters!, 'door_glass_panel_1')
        const newTotalDoorWidth = (doorPanel1?.width || 0) + numValue
        model.doorConfiguration = {
          ...model.doorConfiguration!,
          totalDoorWidth: newTotalDoorWidth,
          individualDoorWidth: [doorPanel1?.width || 0, numValue],
        }
        updateParam(model, 'top_door_glass_panel', { width: newTotalDoorWidth })
        model.width = model.width! + widthDifference
        updateParam(model, 'top_frame_profile', { width: model.width! })
      } else if (positionId === 'left_glass_panel') {
        const leftFrame = getParam(model.parameters!, 'left_frame_profile')
        if (leftFrame) {
          updateParam(model, 'bottom_left_frame_profile', {
            width: numValue + leftFrame.width,
          })
        }
        model.width = model.width! + widthDifference
        updateParam(model, 'top_frame_profile', { width: model.width! })
      } else if (positionId === 'right_glass_panel') {
        const rightFrame = getParam(model.parameters!, 'right_frame_profile')
        if (rightFrame) {
          updateParam(model, 'bottom_right_frame_profile', {
            width: numValue + rightFrame.width,
          })
        }
        model.width = model.width! + widthDifference
        updateParam(model, 'top_frame_profile', { width: model.width! })
      }
      recalculateTemplate2Positions(model)
    }
  }

  return model
}

const leftFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')

  if (!bottomLeft || !leftFrame) return model

  const currentLeftWidth = bottomLeft.width
  const widthDifference = numValue - currentLeftWidth

  updateParam(model, 'bottom_left_frame_profile', { width: numValue })
  const newLeftPanelWidth = numValue - leftFrame.width
  updateParam(model, 'left_glass_panel', { width: newLeftPanelWidth })

  model.width = model.width! + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width! })

  const templateType = detectTemplate(model)
  if (templateType === 'template2') {
    recalculateTemplate2Positions(model)
  } else {
    recalculateTemplate1Positions(model)
  }

  return model
}

const rightFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')

  if (!bottomRight || !rightFrame) return model

  const currentRightWidth = bottomRight.width
  const widthDifference = numValue - currentRightWidth

  updateParam(model, 'bottom_right_frame_profile', { width: numValue })
  const newRightPanelWidth = numValue - rightFrame.width
  updateParam(model, 'right_glass_panel', { width: newRightPanelWidth })

  model.width = model.width! + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width! })

  const templateType = detectTemplate(model)
  if (templateType === 'template2') {
    recalculateTemplate2Positions(model)
  } else {
    recalculateTemplate1Positions(model)
  }

  return model
}

// ─── Template2-specific dimension handlers ───

const template2TotalWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const currentTotalWidth = model.width!
  const widthDifference = numValue - currentTotalWidth
  const halfDifference = widthDifference / 2

  model.width = numValue
  updateParam(model, 'top_frame_profile', { width: numValue })

  if (cascadeUpdates) {
    const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
    const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')

    if (bottomLeft && bottomRight) {
      updateParam(model, 'bottom_left_frame_profile', {
        width: bottomLeft.width + halfDifference,
      })
      updateParam(model, 'bottom_right_frame_profile', {
        width: bottomRight.width + halfDifference,
      })
    }

    const leftPanel = getParam(model.parameters!, 'left_glass_panel')
    const rightPanel = getParam(model.parameters!, 'right_glass_panel')

    if (leftPanel) {
      updateParam(model, 'left_glass_panel', { width: leftPanel.width + halfDifference })
    }
    if (rightPanel) {
      updateParam(model, 'right_glass_panel', { width: rightPanel.width + halfDifference })
    }

    recalculateTemplate2Positions(model)
  }

  return model
}

const template2TotalHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options

  model.height = numValue
  updateParam(model, 'left_frame_profile', { height: numValue })
  updateParam(model, 'right_frame_profile', { height: numValue })

  if (cascadeUpdates) {
    const topDoorPanel = getParam(model.parameters!, 'top_door_glass_panel')
    const topFrame = getParam(model.parameters!, 'top_frame_profile')

    if (topDoorPanel && topFrame) {
      const topDoorHeight = topDoorPanel.height
      const remainingHeight = numValue - topFrame.height * 2
      const remainingDoorHeight = numValue - topDoorHeight - topFrame.height

      updateParam(model, 'left_glass_panel', { height: remainingHeight })
      updateParam(model, 'right_glass_panel', { height: remainingHeight })
      updateParam(model, 'door_glass_panel_1', { height: remainingDoorHeight })
      updateParam(model, 'door_glass_panel_2', { height: remainingDoorHeight })
    }
  }

  return model
}

const topDoorHeight: DimensionHandler = (model, _initial, numValue) => {
  const topDoorPanel = getParam(model.parameters!, 'top_door_glass_panel')
  const doorPanel1 = getParam(model.parameters!, 'door_glass_panel_1')
  const doorPanel2 = getParam(model.parameters!, 'door_glass_panel_2')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!topDoorPanel || !doorPanel1 || !doorPanel2 || !topFrame) return model

  const heightDifference = numValue - topDoorPanel.height

  updateParam(model, 'top_door_glass_panel', { height: numValue })

  const newDoorHeight = doorPanel1.height - heightDifference
  updateParam(model, 'door_glass_panel_1', { height: newDoorHeight })
  updateParam(model, 'door_glass_panel_2', { height: newDoorHeight })

  const newDoorY = topFrame.height + numValue
  updateParam(model, 'door_glass_panel_1', {
    position: { ...doorPanel1.position, y: newDoorY },
  })
  updateParam(model, 'door_glass_panel_2', {
    position: { ...doorPanel2.position, y: newDoorY },
  })

  return model
}

const doorPanelHeight: DimensionHandler = (model, _initial, numValue) => {
  const doorPanel1 = getParam(model.parameters!, 'door_glass_panel_1')
  const doorPanel2 = getParam(model.parameters!, 'door_glass_panel_2')
  const topDoorPanel = getParam(model.parameters!, 'top_door_glass_panel')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!doorPanel1 || !doorPanel2 || !topDoorPanel || !topFrame) return model

  const heightDifference = numValue - doorPanel1.height

  updateParam(model, 'door_glass_panel_1', { height: numValue })
  updateParam(model, 'door_glass_panel_2', { height: numValue })

  const newTopHeight = topDoorPanel.height - heightDifference
  updateParam(model, 'top_door_glass_panel', { height: newTopHeight })

  const newDoorY = topFrame.height + newTopHeight
  updateParam(model, 'door_glass_panel_1', {
    position: { ...doorPanel1.position, y: newDoorY },
  })
  updateParam(model, 'door_glass_panel_2', {
    position: { ...doorPanel2.position, y: newDoorY },
  })

  return model
}

// ─── Universal dimension handler (auto-detects template) ───

function createUniversalHandler(
  template1Handler: DimensionHandler,
  template2Handler: DimensionHandler,
): DimensionHandler {
  return (model, initialModel, numValue, options) => {
    const templateType = detectTemplate(model)
    if (templateType === 'template2') {
      return template2Handler(model, initialModel, numValue, options)
    }
    return template1Handler(model, initialModel, numValue, options)
  }
}

export const doorWindowModelDimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth: createUniversalHandler(template1TotalWidth, template2TotalWidth),
  totalHeight: createUniversalHandler(template1TotalHeight, template2TotalHeight),
  topCenterHeight,
  centerPanelHeight,
  parameterWidth,
  leftFrameWidth,
  rightFrameWidth,
  topDoorHeight,
  doorPanelHeight,
}
